import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  TextField,
} from "@mui/material";
import { parsePhoneNumber } from "awesome-phonenumber";
import React from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import sv from "react-phone-number-input/locale/sv.json";
import "react-phone-number-input/style.css";

import CustomPhoneNumber from "./components/PhoneNumber";
import { getRtkErrorMessage } from "../../../lib/utils/rtkErrorHelper";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import { CommunicationTypeEnum, CustomerCommunication } from "../../../store/types/communication.types";
import { usePostUsersCommunicationsMutation } from "../../../store/services/communicationService";

type ErrorType =
  | "no-error"
  | "too-short"
  | "too-long"
  | "invalid-phone"
  | "invalid-mobile"
  | "invalid-email";

interface FormValues {
  value: string;
  description: string;
}

interface FormErrors {
  value: ErrorType;
  description: ErrorType;
}

interface UpdateCommunicationDialogProps {
  customerId: number;
  type?: CommunicationTypeEnum;
  onClose: () => void;
  editing?: CustomerCommunication;
  contactPersonNo?: number;
}

const ERROR_MESSAGES: Record<ErrorType, string> = {
  "no-error": "",
  "too-short": "Värdet måste vara längre",
  "too-long": "Värdet är för långt",
  "invalid-phone": "Inget giltigt telefonnummer",
  "invalid-mobile": "Inget giltigt mobilnummer",
  "invalid-email": "Ingen giltig e-post",
};

const COMMUNICATION_LABELS: Record<CommunicationTypeEnum, string> = {
  [CommunicationTypeEnum.Telefon]: "Telefon",
  [CommunicationTypeEnum.Mobiltelefon]: "Mobil",
  [CommunicationTypeEnum.Epost]: "E-post",
  [CommunicationTypeEnum.Facebook]: "Facebook",
  [CommunicationTypeEnum.Twitter]: "Twitter",
  [CommunicationTypeEnum.CompanyContact]: "Företagskontakt",
  [CommunicationTypeEnum.Brev]: "Brev",
  [CommunicationTypeEnum.EndastPdf]: "Endast PDF",
};

const validateEmail = (email: string): boolean => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export default function UpdateCommunicationDialog({
  customerId,
  type,
  onClose,
  editing,
  contactPersonNo,
}: UpdateCommunicationDialogProps) {
  const [updateCommunication, { isLoading: isUpdating, error }] =
    usePostUsersCommunicationsMutation();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const [values, setValues] = React.useState<FormValues>({
    value: editing?.Value ?? "",
    description: editing?.Description ?? "",
  });

  const [errors, setErrors] = React.useState<FormErrors>({
    value: "no-error",
    description: "no-error",
  });

  const [onlyInvoiceMail, setOnlyInvoiceMail] = React.useState(
    editing?.IsNoExternal ?? false
  );
  const [changed, setChanged] = React.useState(false);

  const validateForm = React.useCallback(() => {
    if (!values.value) {
      return {
        value: "too-short" as ErrorType,
        description: "no-error" as ErrorType,
      };
    }

    let valueError: ErrorType = "no-error";
    const phone = parsePhoneNumber(
      values.value.replace(/^0{2}/, "+").replace(/^0/, "+46"),
      { regionCode: "SE" }
    );

    if (type === CommunicationTypeEnum.Telefon && !phone.valid) {
      valueError = "invalid-phone";
    } else if (
      type === CommunicationTypeEnum.Mobiltelefon &&
      !phone.typeIsMobile
    ) {
      valueError = "invalid-mobile";
    } else if (
      type === CommunicationTypeEnum.Epost &&
      !validateEmail(values.value)
    ) {
      valueError = "invalid-email";
    }

    const descriptionError: ErrorType =
      values.description.length > 100 ? "too-long" : "no-error";

    return { value: valueError, description: descriptionError };
  }, [values, type]);

  React.useEffect(() => {
    if (changed) {
      setErrors(validateForm());
    }
  }, [values, changed, validateForm]);

  const handleClose = () => {
    setValues({ value: "", description: "" });
    setErrors({ value: "no-error", description: "no-error" });
    setChanged(false);
    onClose();
  };

  const handleUpdate = async () => {
    const communication: Partial<CustomerCommunication> = {
      ...(editing && { CommunicationNo: editing.CommunicationNo }),
      Type: type,
      Value: values.value,
      Description: values.description,
      IsPrimary: editing?.IsPrimary ?? false,
      Invoice: editing?.Invoice ?? false,
      Sms: editing?.Sms ?? false,
      SetByCustomer: editing?.SetByCustomer ?? false,
      Status: editing?.Status ?? true,
      IsNoExternal: onlyInvoiceMail,
      ...(contactPersonNo && { ContactPersonNo: contactPersonNo }),
    };

    try {
      await updateCommunication({
        customerCommunication: [communication],
        customerNo: customerId,
      }).unwrap();
      handleClose();
    } catch (err) {
      const error = err as FetchBaseQueryError | SerializedError;
      // Hantera olika typer av fel
      console.log(error);
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else if (typeof error === "string") {
        setErrorMessage(error);
      } else {
        setErrorMessage(getRtkErrorMessage(error));
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (
      e.key === "Enter" &&
      changed &&
      !Object.values(errors).some((e) => e !== "no-error")
    ) {
      handleUpdate();
    }
  };

  const isPhoneInput =
    type === CommunicationTypeEnum.Mobiltelefon ||
    type === CommunicationTypeEnum.Telefon;
  const showInvoiceMailOption =
    type === CommunicationTypeEnum.Epost && editing?.IsPrimary;

  if (!type) return null;

  return (
    <Dialog open={!!type || !!editing} onClose={handleClose}>
      <DialogTitle>Kontaktväg</DialogTitle>
      <DialogContent>
        {(error || errorMessage) && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setErrorMessage(null)}
          >
            {errorMessage || "Ett fel uppstod vid sparande. Försök igen."}
          </Alert>
        )}

        {isPhoneInput ? (
          <PhoneInput
            labels={sv}
            countries={["SE", "NO", "FI", "DE", "DK"]}
            autoFocus
            defaultCountry="SE"
            placeholder="Telefonnummer"
            value={values.value}
            onChange={(value) => {
              setValues((prev) => ({ ...prev, value: value || "" }));
              setChanged(true);
            }}
            inputComponent={CustomPhoneNumber}
            error={values.value ? !isValidPhoneNumber(values.value) : true}
            helperText={
              values.value
                ? isValidPhoneNumber(values.value)
                  ? undefined
                  : "Ogiltigt telefonnummer"
                : "Ange ett telefonnummer"
            }
          />
        ) : (
          <TextField
            error={changed && errors.value !== "no-error"}
            sx={{ minWidth: 300 }}
            autoFocus
            margin="dense"
            label={COMMUNICATION_LABELS[type]}
            fullWidth
            value={values.value}
            onChange={(e) => {
              setValues((prev) => ({ ...prev, value: e.target.value }));
              setChanged(true);
            }}
            onKeyUp={handleKeyPress}
            helperText={changed && ERROR_MESSAGES[errors.value]}
          />
        )}

        <TextField
          error={changed && errors.description !== "no-error"}
          sx={{ minWidth: 300 }}
          margin="dense"
          label="Beskrivning"
          fullWidth
          value={values.description}
          onChange={(e) => {
            setValues((prev) => ({ ...prev, description: e.target.value }));
            setChanged(true);
          }}
          onKeyUp={handleKeyPress}
          helperText={changed && ERROR_MESSAGES[errors.description]}
        />

        {showInvoiceMailOption && (
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={onlyInvoiceMail}
                  onChange={(e) => {
                    setOnlyInvoiceMail(e.target.checked);
                    setChanged(true);
                  }}
                />
              }
              label="Endast fakturor"
            />
          </FormGroup>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Avbryt
        </Button>
        <Button
          onClick={handleUpdate}
          color="primary"
          disabled={
            Object.values(errors).some((e) => e !== "no-error") ||
            !changed ||
            isUpdating
          }
        >
          {isUpdating ? "Sparar..." : "Spara"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
